// frontend/src/pages/Admin/tabs/MembersTab.tsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import styles from "./MembersTab.module.css";
import EditMember from "../../../components/Members/EditMember";
import AddMember from "../../../components/Members/AddMember";

export default function MembersTab() {
  const [members, setMembers] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<any>(null);
  const [viewing, setViewing] = useState<any>(null);
  const [showAdd, setShowAdd] = useState(false);

  const pageSize = 12;
  const [totalPages, setTotalPages] = useState(1);

  const API = process.env.REACT_APP_API_URL || "http://localhost:8080";

  const token = localStorage.getItem("bni_token");
  const user = JSON.parse(localStorage.getItem("bni_user") || "null");

  const loadMembers = () => {
    axios
      .get(`${API}/api/members?page=${page}&pageSize=${pageSize}`)
      .then((r) => {
        setMembers(r.data.data || []);
        setTotalPages(r.data.pagination.totalPages || 1);
      })
      .catch((error) => {
        console.log("error while getting Members detail", error);
      });
  };

  useEffect(() => {
    loadMembers();
  }, [page]);

  const deleteMember = async (id: string) => {
    if (!window.confirm("Delete this member?")) return;
    try {
      await axios.delete(`${API}/api/members/${id}`, {
        headers: { Authorization: "Bearer " + token },
      });

      loadMembers();
    } catch (error) {}
  };

  return (
    <div className={styles.wrapper}>
      {/* SECTION HEADER */}
      <div className={styles.header}>
        <h2>Manage Members</h2>

        <button className={styles.addBtn} onClick={() => setShowAdd(true)}>
          + Add Member
        </button>
      </div>

      {/* MEMBERS GRID */}
      <div className={styles.grid}>
        {members.map((m) => (
          <div
            key={m._id}
            className={styles.card}
            onClick={() => setViewing(m)}
          >
            <img src={m.userImage} className={styles.avatar} alt="" />

            <div className={styles.cardData}>
              <h3>{m.name}</h3>
              <p className={styles.orgName}>{m.orgName || "—"}</p>

              <span className={styles.roleTag}>{m.memberType}</span>
            </div>

            <div className={styles.btnRow} onClick={(e) => e.stopPropagation()}>
              <button className={styles.editBtn} onClick={() => setEditing(m)}>
                Edit
              </button>

              <button
                className={styles.deleteBtn}
                onClick={() => deleteMember(m._id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}
      <div className={styles.pagination}>
        <button
          className={styles.pageBtn}
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          Prev
        </button>

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => setPage(i + 1)}
            className={`${styles.pageNum} ${
              page === i + 1 ? styles.active : ""
            }`}
          >
            {i + 1}
          </button>
        ))}

        <button
          className={styles.pageBtn}
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
        >
          Next
        </button>
      </div>

      {/* EDIT MODAL */}
      {editing && (
        <EditMember
          member={editing}
          isSuperAdmin={user?.role === "SUPER_ADMIN"}
          onClose={() => setEditing(null)}
          onSaved={loadMembers}
        />
      )}

      {/* VIEW MODAL */}
      {viewing && (
        <ViewMemberModal member={viewing} onClose={() => setViewing(null)} />
      )}

      {showAdd && (
        <div className={styles.modalBackdrop}>
          <div className={`${styles.modalCard} ${styles.addModal}`}>
            <div className={styles.modalHeader}>
              Add New Member
              <button
                className={styles.closeBtn}
                onClick={() => setShowAdd(false)}
              >
                ✖
              </button>
            </div>

            <div className={styles.modalContent}>
              <AddMember
                onClose={() => setShowAdd(false)}
                onSaved={() => {
                  setShowAdd(false);
                  loadMembers();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ✅ VIEW MEMBER MODAL */
function ViewMemberModal({ member, onClose }: any) {
  const API = process.env.REACT_APP_API_URL || "http://localhost:8080";
  const token = localStorage.getItem("bni_token");
  const user = JSON.parse(localStorage.getItem("bni_user") || "null");

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const [fields, setFields] = useState({
    memberType: member.memberType,
  });

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modalCard}>
        <div className={styles.modalHeader}>
          Member Details
          <button className={styles.closeBtn} onClick={onClose}>
            ✖
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* IMAGE */}
          <img
            src={member.userImage}
            className={styles.modalAvatar}
            alt="profile"
          />

          <h2 style={{ textAlign: "center", fontWeight: 800 }}>
            {member.name}
          </h2>

          <p className={styles.modalRole}>{member?.memberType}</p>

          <div className={styles.sectionTitle}>Basic Information</div>
          <div className={styles.modalInfoGrid}>
            <div>
              <strong>Email:</strong> {member.email || "—"}
            </div>
            <div>
              <strong>Contact:</strong> {member.contactNo || "—"}
            </div>
            <div>
              <strong>WhatsApp:</strong> {member.whatsappNo || "—"}
            </div>
          </div>

          <div className={styles.sectionTitle}>Company</div>
          <div className={styles.modalInfoGrid}>
            <div>
              <strong>Organisation:</strong> {member.orgName || "—"}
            </div>
            <div>
              <strong>Website:</strong> {member.website || "—"}
            </div>
            <div>
              <strong>Address:</strong> {member.address || "—"}
            </div>
            <div>
              <strong>Company Details:</strong> {member.companyDetails || "—"}
            </div>
          </div>

          <div className={styles.sectionTitle}>Social Links</div>
          <div className={styles.modalInfoGrid}>
            <div>
              <strong>Facebook:</strong>{" "}
              {member?.socialHandles?.facebook || "—"}
            </div>
            <div>
              <strong>Instagram:</strong>{" "}
              {member?.socialHandles?.instagram || "—"}
            </div>
            <div>
              <strong>LinkedIn:</strong>{" "}
              {member?.socialHandles?.linkedin || "—"}
            </div>
          </div>

          <div className={styles.sectionTitle}>About</div>
          <div className={styles.modalInfoGrid}>
            <div>
              <strong>Bio:</strong> {member.bio || "—"}
            </div>
            <div>
              <strong>Org Bio:</strong> {member.orgBio || "—"}
            </div>
            <div>
              <strong>Recognition:</strong> {member.recognition || "None"}
            </div>
          </div>

          {/* SUPER ADMIN SECTION */}
          {isSuperAdmin && (
            <>
              <div className={styles.sectionTitle}>Admin Controls</div>

              <label className={styles.fieldLabel}>BNI Role</label>
              <select
                className={styles.adminSelect}
                value={fields.memberType}
                onChange={(e) =>
                  setFields({ ...fields, memberType: e.target.value })
                }
              >
                <option value="Member">Member</option>
                <option value="Vice President">Vice President</option>
                <option value="President">President</option>
                <option value="Secretary Treasurer">Secretary Treasurer</option>
                <option value="Leadership">Leadership</option>
              </select>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
